import React from "react";
import moment from "moment";

import StatusBadge from "designV2/components/StatusBadge";
import {
  DetailField,
  CopyableField,
  ImageStrip,
  Stars,
  assetUrl,
  humanize,
} from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

const formatDateTime = (value) =>
  value ? moment(value).format("MMM D, YYYY [at] hh:mm A") : null;
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : null);

/**
 * DriverDetails - content-only, rendered inside a DialogShell.
 *
 * The previous version drew its own header and close button, and put the
 * driver's vehicles behind a second nested dialog. A driver's vehicles
 * are the main thing you open this for, so they are listed inline.
 */
const DriverDetails = ({ driver }) => {
  if (!driver) return null;

  const licence = driver.drivingLicence || {};
  const licenceExpired = licence.expireDate
    ? moment(licence.expireDate).isBefore(moment())
    : false;
  const cars = driver.car || [];
  const defaultCarId = driver.defaultCar && driver.defaultCar._id;

  return (
    <div className="superPersonDetails">
      <div className="superPersonDetailsTop">
        <StatusBadge
          status={driver.state === "SUSPENDED" ? "danger" : "success"}
          label={humanize(driver.state) || "Unknown"}
        />
        <StatusBadge
          status={driver.isVerified ? "success" : "warning"}
          label={driver.isVerified ? "Verified" : "Not verified"}
          dot={false}
        />
        {driver.workStatus && (
          <span className="superPersonTag">{humanize(driver.workStatus)}</span>
        )}
        {licenceExpired && (
          <StatusBadge status="danger" label="Licence expired" dot={false} />
        )}
        <span className="superPersonDetailsHeadline">{driver.fullName || "Unnamed"}</span>
      </div>

      <div className="superPersonGrid">
        <CopyableField label="Driver ID" value={driver._id} />
        <DetailField label="Rating" value={<Stars value={driver.averageRate} />} />
        <DetailField label="Phone" value={driver.phoneNumber} />
        <DetailField label="Email" value={driver.email} />
        <DetailField label="Gender" value={humanize(driver.gender)} />
        <DetailField label="Date of birth" value={formatDate(driver.birthDate)} />
        <DetailField label="Ratings received" value={driver.numberOfRates} />
        <DetailField label="Joined" value={formatDateTime(driver.createdAt)} />
        <DetailField
          label="Address"
          value={
            driver.address && driver.address.full
              ? `${driver.address.full}${driver.address.zipCode ? ` · ${driver.address.zipCode}` : ""}`
              : null
          }
          wide
        />
        <CopyableField label="Stripe account" value={driver.stripeAccountId} />
        <DetailField
          label="Notifications"
          value={driver.hasNotification ? "Enabled" : "Disabled"}
        />
      </div>

      <div className="superPersonGrid">
        <CopyableField label="Licence number" value={licence.licenceId} />
        <DetailField
          label="Licence expires"
          value={
            licence.expireDate ? (
              <span style={{ color: licenceExpired ? "var(--color-accent-danger-fg)" : undefined }}>
                {formatDate(licence.expireDate)}
              </span>
            ) : null
          }
        />
      </div>

      <ImageStrip
        label="Driving licence"
        urls={(Array.isArray(licence.photoUrl) ? licence.photoUrl : [licence.photoUrl]).map(assetUrl)}
      />

      {cars.length > 0 && (
        <div className="superPersonField">
          <span className="superPersonLabel">Vehicles ({cars.length})</span>
          <div className="superPersonList">
            {cars.map((car) => (
              <div className="superPersonListRow" key={car._id}>
                <span className="superPersonListText">
                  <span className="superPersonListName">
                    {[car.brand && car.brand.name, car.model && car.model.name]
                      .filter(Boolean)
                      .join(" ") || "Unspecified"}
                    {car._id === defaultCarId ? " · default" : ""}
                  </span>
                  <span className="superPersonListMeta">
                    {[
                      car.plate,
                      car.color && car.color.name,
                      car.carType && car.carType.type,
                      car.manufacturingYear,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="superPersonRow" style={{ marginLeft: "auto" }}>
                  {car.ride && <span className="superPersonTag">Rides</span>}
                  {car.delivery && <span className="superPersonTag">Deliveries</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDetails;
